import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, asArray } from "@/lib/api";
import { SeverityBadge, StatusBadge } from "@/components/Primitives";
import { useStatuses } from "@/context/StatusContext";
import { slaCountdown } from "@/lib/slaCountdown";
import NewTicketDialog from "@/components/NewTicketDialog";
import { Search, Plus, Link2, GitBranch, Star } from "lucide-react";

const PAGE = 50;
const TABS = [
  { key: "list", label: "Tickets" },
  { key: "workload", label: "Workload" },
  { key: "sla", label: "SLA Performance" },
];

export default function Tickets() {
  const [params, setParams] = useSearchParams();
  const { statuses, byKey } = useStatuses();
  const [body, setBody] = useState({ items: [], total: 0, offset: 0, limit: PAGE });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [workload, setWorkload] = useState(null);
  const [perf, setPerf] = useState(null);
  const [names, setNames] = useState({ clients: {}, users: {} });
  const tab = params.get("tab") || "list";

  // Apply several param changes as ONE setParams call. Two calls built from
  // the same render-time `params` each start from the pre-update snapshot,
  // so the second silently overwrote the first -- the Workload drill-through
  // set "assignee" then set "tab" and only the tab change ever landed.
  const setMany = (kv) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(kv)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    if (!Object.prototype.hasOwnProperty.call(kv, "offset")) next.delete("offset");   // a new filter starts at page 1
    setParams(next, { replace: true });
  };
  const set = (k, v) => setMany({ [k]: v });

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    for (const k of ["status", "priority", "assignee", "client_id", "q"]) {
      if (params.get(k)) qs.set(k, params.get(k));
    }
    qs.set("limit", String(PAGE));
    qs.set("offset", params.get("offset") || "0");
    api.get(`/tickets?${qs}`)
      .then((r) => setBody(r.data && typeof r.data === "object" && r.data.items
        ? r.data : { items: asArray(r.data), total: asArray(r.data).length, offset: 0, limit: PAGE }))
      .catch(() => setBody({ items: [], total: 0, offset: 0, limit: PAGE }))
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => { if (tab === "list") load(); }, [load, tab]);
  useEffect(() => {
    if (tab === "workload" && !workload) {
      api.get("/desk/workload").then((r) => setWorkload(r.data)).catch(() => setWorkload({ error: true }));
    }
    if (tab === "sla" && !perf) {
      api.get("/desk/sla-performance").then((r) => setPerf(r.data)).catch(() => setPerf({ error: true }));
    }
  }, [tab, workload, perf]);

  // Resolve names once, not per row. A portal account is refused /users (403);
  // the catch below degrades that column to a dash rather than breaking the page.
  useEffect(() => {
    Promise.all([api.get("/clients").catch(() => ({ data: [] })),
                 api.get("/users").catch(() => ({ data: [] }))])
      .then(([c, u]) => setNames({
        clients: Object.fromEntries(asArray(c.data).map((x) => [x.id, x.name])),
        users: Object.fromEntries(asArray(u.data).map((x) => [x.id, x.name])),
      }));
  }, []);

  const offset = Number(params.get("offset") || 0);
  const pages = Math.max(1, Math.ceil(body.total / PAGE));
  const page = Math.floor(offset / PAGE) + 1;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Service Desk · PSA</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">
            Tickets {tab === "list" && <span className="text-zinc-500">({body.total})</span>}
          </h1>
        </div>
        <button onClick={() => setDialog(true)} data-testid="new-ticket-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5 shrink-0">
          <Plus size={15} /> New ticket
        </button>
      </div>

      <div className="flex gap-1 border-b border-zinc-900">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => set("tab", t.key === "list" ? "" : t.key)}
            data-testid={`tickets-tab-${t.key}`}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.key
              ? "border-blue-500 text-zinc-100" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "list" && (
        <>
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input defaultValue={params.get("q") || ""}
                onKeyDown={(e) => e.key === "Enter" && set("q", e.target.value)}
                onBlur={(e) => set("q", e.target.value)}
                placeholder="Search titles, then Enter" data-testid="ticket-search"
                className="bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm w-full focus:outline-none focus:border-blue-500/60" />
            </div>
            <select value={params.get("status") || ""} onChange={(e) => set("status", e.target.value)}
              data-testid="ticket-status-filter"
              className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm min-w-0 max-w-[10rem] shrink">
              <option value="">All status</option>
              {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select value={params.get("priority") || ""} onChange={(e) => set("priority", e.target.value)}
              data-testid="ticket-priority-filter"
              className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm min-w-0 max-w-[10rem] shrink">
              <option value="">All priority</option>
              {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-4 py-3 w-20">#</th>
                  <th className="text-left px-4 py-3">Title</th>
                  <th className="text-left px-4 py-3">Client</th>
                  <th className="text-left px-4 py-3">Requester</th>
                  <th className="text-left px-4 py-3">Assignee</th>
                  <th className="text-left px-4 py-3">Priority</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">SLA</th>
                  <th className="text-left px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={9} className="px-4 py-6 text-zinc-500">Loading…</td></tr>}
                {!loading && !body.items.length && (
                  <tr><td colSpan={9} className="px-4 py-6 text-zinc-500">No tickets match these filters.</td></tr>
                )}
                {!loading && body.items.map((t) => {
                  const sla = slaCountdown(t, byKey[t.status]);
                  return (
                    <tr key={t.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                      <td className="px-4 py-2 font-mono text-zinc-500 text-xs">#{t.number}</td>
                      <td className="px-4 py-2">
                        <Link to={`/tickets/${t.id}`} className="hover:text-blue-400 inline-flex items-center gap-1.5" data-testid={`ticket-link-${t.id}`}>
                          {/* Sub-project I, Task 9: on production today NO
                              ticket carries a parent_id, so this renders on
                              zero rows there -- deliberately no icon at all
                              rather than a dash on every row, which the
                              standing rule about incomplete columns exists
                              to avoid. "Is a child" costs nothing extra
                              (parent_id is already on every row); "has
                              children" is a single indexed per-page query
                              (server.py's list_tickets), added only once
                              {"{tenant_id, parent_id}"} had a real index
                              behind it -- see that route's own comment. */}
                          {t.parent_id && (
                            <Link2 size={12} className="text-zinc-500 shrink-0" title="This ticket is a child of another ticket" />
                          )}
                          {t.has_children && (
                            <GitBranch size={12} className="text-zinc-500 shrink-0" title="This ticket has child tickets" />
                          )}
                          {t.vip && (
                            <Star size={12} className="text-amber-400 shrink-0" fill="currentColor"
                                  title="Raised by a VIP contact" data-testid={`ticket-vip-${t.id}`} />
                          )}
                          {t.title}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-zinc-400">{names.clients[t.client_id] || "—"}</td>
                      <td className="px-4 py-2 text-zinc-400">
                        {typeof t.requester_name === "string" ? t.requester_name : "—"}
                      </td>
                      <td className="px-4 py-2 text-zinc-400">{t.assignee_id ? (names.users[t.assignee_id] || "—") : "Unassigned"}</td>
                      <td className="px-4 py-2"><SeverityBadge level={t.priority} /></td>
                      <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
                      <td className="px-4 py-2"><StatusBadge status={t.status} tone={sla.tone} label={sla.text} /></td>
                      <td className="px-4 py-2 font-mono text-[11px] text-zinc-500">{(t.updated_at || t.created_at)?.slice(0, 10)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center gap-3 text-sm">
              <button disabled={offset <= 0} data-testid="page-prev"
                onClick={() => set("offset", String(Math.max(0, offset - PAGE)))}
                className="border border-zinc-800 rounded px-3 py-1 disabled:opacity-40">Previous</button>
              <span className="text-zinc-500 font-mono text-xs">Page {page} of {pages} · {body.total} tickets</span>
              <button disabled={page >= pages} data-testid="page-next"
                onClick={() => set("offset", String(offset + PAGE))}
                className="border border-zinc-800 rounded px-3 py-1 disabled:opacity-40">Next</button>
            </div>
          )}
        </>
      )}

      {tab === "workload" && <WorkloadTab data={workload} onPick={(id) => setMany({ assignee: id, tab: "" })} />}
      {tab === "sla" && <SlaTab data={perf} />}

      {dialog && (
        <NewTicketDialog
          onClose={() => setDialog(false)}
          onCreated={() => { setDialog(false); load(); }}
        />
      )}
    </div>
  );
}

function WorkloadTab({ data, onPick }) {
  if (!data) return <div className="text-zinc-500 text-sm">Loading…</div>;
  if (data.error) return <div className="text-red-400 text-sm">Could not load workload.</div>;
  const rows = asArray(data.technicians || data.rows || data);
  return (
    <div className="space-y-3">
      {typeof data.unassigned === "number" && (
        <div className={`rounded-md px-4 py-2 text-sm border ${data.unassigned
          ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
          : "bg-zinc-950 border-zinc-900 text-zinc-500"}`} data-testid="workload-unassigned">
          {data.unassigned} unassigned open ticket{data.unassigned === 1 ? "" : "s"}
        </div>
      )}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <tr><th className="text-left px-4 py-3">Technician</th><th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Open</th><th className="text-left px-4 py-3">Past deadline</th></tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} onClick={() => onPick(u.id)} data-testid={`workload-row-${u.id}`}
                className="border-b border-zinc-900 hover:bg-zinc-900/40 cursor-pointer">
                <td className="px-4 py-2">{u.name}</td>
                <td className="px-4 py-2 text-zinc-500 font-mono text-xs">{u.role?.replaceAll("_", " ")}</td>
                <td className="px-4 py-2 font-mono">{u.open_tickets}</td>
                <td className={`px-4 py-2 font-mono ${u.past_deadline ? "text-red-400" : "text-zinc-500"}`}>{u.past_deadline}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SlaTab({ data }) {
  if (!data) return <div className="text-zinc-500 text-sm">Loading…</div>;
  if (data.error) return <div className="text-red-400 text-sm">Could not load SLA performance.</div>;
  // Every percentage renders its own n. response_met_pct is currently computed
  // over ONE ticket (first_response_at is set on 1 of 35), and "100%" without
  // "(n=1)" is exactly the fiction this page exists to remove.
  const Stat = ({ label, pct, n, unit = "" }) => (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className="text-2xl font-display mt-1">
        {pct === null || pct === undefined ? "—" : `${pct}${unit}`}
      </div>
      <div className="text-[11px] text-zinc-500 font-mono mt-1">
        n = {n ?? 0}{(n ?? 0) < 5 ? " — too few to be meaningful" : ""}
      </div>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Resolution met" pct={data.resolution_met_pct} n={data.closed} unit="%" />
        <Stat label="Response met" pct={data.response_met_pct}
          n={data.tickets - (data.awaiting_first_response ?? 0)} unit="%" />
        <Stat label="Median resolution" pct={data.median_resolution_business_minutes} n={data.closed} unit=" min" />
        <Stat label="Median first response" pct={data.median_first_response_business_minutes}
          n={data.tickets - (data.awaiting_first_response ?? 0)} unit=" min" />
      </div>
      <div className="text-xs text-zinc-500">
        Window: {data.window_days} days · {data.tickets} tickets · {data.open_past_deadline} open past deadline
        · {data.awaiting_first_response} awaiting a first response · measured in {data.measured_in}.
      </div>
    </div>
  );
}
